import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { stripImmutable } from '../lib/orgScope';

const COMPANY_BUCKET = 'company_attachments';
const USER_BUCKET    = 'user_attachments';

export interface Company {
  id: string;
  org_id: string;
  project_id: string | null;
  name: string;
  details: string | null;
  owner_customer_user_id: string | null;
  assignee_id: string | null;
  domain: string | null;
  employee_size: number | null;
  headquarters: string | null;
  industry: string | null;
  revenue: number | null;
  website: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyAttachment {
  id: string;
  company_id: string;
  org_id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  url: string;
  created_by: string;
  created_at: string;
}

export interface CustomerUser {
  id: string;
  org_id: string;
  project_id: string | null;
  company_id: string | null;
  name: string;
  details: string | null;
  assignee_id: string | null;
  email: string | null;
  phone: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  companies?: { id: string; name: string } | null;
}

export interface CustomerUserAttachment {
  id: string;
  customer_user_id: string;
  org_id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  url: string;
  created_by: string;
  created_at: string;
}

// Org id for inserts; RLS validates it against the caller's membership.
function requireOrgId(): string {
  const orgId = orgStore.getOrgId();
  if (!orgId) throw new Error('No organization found.');
  return orgId;
}

let _companiesCache: { key: string; data: Company[]; expiry: number } | null = null;
let _usersCache: { key: string; data: CustomerUser[]; expiry: number } | null = null;
const CACHE_TTL = 60_000;

function invalidateCompaniesCache() { _companiesCache = null; }
function invalidateUsersCache() { _usersCache = null; }

// Throws if there's no signed-in user. Used by every code path that
// creates a row whose `created_by` column is NOT NULL — silently
// inserting an empty string would later violate the FK on the user
// and surface as a cryptic 23502 / 23503 in the Supabase error log.
async function requireCurrentUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) {
    throw new Error('You must be signed in to perform this action.');
  }
  return user.id;
}

export const customerService = {
  // ── Companies ──────────────────────────────────────────────
  async getCompanies(projectId?: string | null): Promise<Company[]> {
    const key = projectId ?? '__all__';
    const now = Date.now();
    if (_companiesCache && _companiesCache.key === key && now < _companiesCache.expiry) {
      return _companiesCache.data;
    }
    let query = supabase.from('companies').select('*').order('created_at', { ascending: false });
    if (projectId) query = query.eq('project_id', projectId);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as Company[];
    _companiesCache = { key, data: rows, expiry: now + CACHE_TTL };
    return rows;
  },

  async getCompany(id: string): Promise<Company> {
    const { data, error } = await supabase.from('companies').select('*').eq('id', id).single();
    if (error) throw error;
    return data as Company;
  },

  async createCompany(name: string, details: string, projectId: string | null, extra?: Partial<Pick<Company, 'owner_customer_user_id' | 'assignee_id' | 'domain' | 'employee_size' | 'headquarters' | 'industry' | 'revenue' | 'website'>>): Promise<Company> {
    const created_by = await requireCurrentUserId();
    const { data, error } = await supabase
      .from('companies')
      .insert({ org_id: requireOrgId(), project_id: projectId ?? null, name, details, created_by, ...extra })
      .select()
      .single();
    if (error) throw error;
    invalidateCompaniesCache();
    return data as Company;
  },

  async updateCompany(id: string, updates: Partial<Pick<Company, 'name' | 'details' | 'owner_customer_user_id' | 'assignee_id' | 'domain' | 'employee_size' | 'headquarters' | 'industry' | 'revenue' | 'website'>>): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .update({ ...stripImmutable(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select()
      .single();
    if (error) throw error;
    invalidateCompaniesCache();
    return data as Company;
  },

  async deleteCompany(id: string): Promise<void> {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
    invalidateCompaniesCache();
  },

  // ── Company Attachments (Supabase Storage) ─────────────────
  async getAttachments(companyId: string): Promise<CompanyAttachment[]> {
    const { data, error } = await supabase
      .from('company_attachments')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async uploadAttachment(companyId: string, orgId: string, file: File): Promise<CompanyAttachment> {
    const ext = file.name.split('.').pop() ?? 'bin';
    const storagePath = `${orgId}/${companyId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from(COMPANY_BUCKET)
      .upload(storagePath, file, { contentType: file.type, upsert: false });
    if (uploadErr) throw new Error(`Storage upload failed: ${uploadErr.message}`);

    const { data: { publicUrl } } = supabase.storage
      .from(COMPANY_BUCKET)
      .getPublicUrl(storagePath);

    // Throws early if not signed in — created_by is a NOT NULL column
    // with a FK to auth.users, so silently inserting '' would 500.
    const userId = await requireCurrentUserId();

    const { data, error: insertErr } = await supabase
      .from('company_attachments')
      .insert({
        company_id: companyId,
        org_id: orgId,
        file_name: file.name,
        file_type: file.type,
        storage_path: storagePath,
        url: publicUrl,
        created_by: userId,
      })
      .select()
      .single();
    if (insertErr) { await supabase.storage.from(COMPANY_BUCKET).remove([storagePath]); throw new Error(`DB insert failed: ${insertErr.message}`); }
    return data;
  },

  async deleteAttachment(attachment: CompanyAttachment): Promise<void> {
    await supabase.storage.from(COMPANY_BUCKET).remove([attachment.storage_path]);
    const { error } = await supabase
      .from('company_attachments')
      .delete()
      .eq('id', attachment.id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  // ── Customer Users (contacts) ──────────────────────────────
  async getCustomerUsers(projectId?: string | null, companyId?: string | null): Promise<CustomerUser[]> {
    const key = `${projectId ?? '__all__'}:${companyId ?? '__all__'}`;
    const now = Date.now();
    if (_usersCache && _usersCache.key === key && now < _usersCache.expiry) {
      return _usersCache.data;
    }
    let query = supabase
      .from('customer_users')
      .select('*, companies:company_id (id, name)')
      .order('created_at', { ascending: false });
    if (projectId) query = query.eq('project_id', projectId);
    if (companyId) query = query.eq('company_id', companyId);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as CustomerUser[];
    _usersCache = { key, data: rows, expiry: now + CACHE_TTL };
    return rows;
  },

  async getCustomerUser(id: string): Promise<CustomerUser> {
    const { data, error } = await supabase
      .from('customer_users')
      .select('*, companies:company_id (id, name)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as CustomerUser;
  },

  async createCustomerUser(
    name: string, details: string, projectId: string | null, companyId: string | null,
    extra?: Partial<Pick<CustomerUser, 'assignee_id' | 'email' | 'phone'>>
  ): Promise<CustomerUser> {
    const created_by = await requireCurrentUserId();
    const { data, error } = await supabase
      .from('customer_users')
      .insert({ org_id: requireOrgId(), project_id: projectId ?? null, company_id: companyId ?? null, name, details, created_by, ...extra })
      .select('*, companies:company_id (id, name)')
      .single();
    if (error) throw error;
    invalidateUsersCache();
    return data as CustomerUser;
  },

  async updateCustomerUser(id: string, updates: Partial<Pick<CustomerUser, 'name' | 'details' | 'company_id' | 'assignee_id' | 'email' | 'phone'>>): Promise<CustomerUser> {
    const { data, error } = await supabase
      .from('customer_users')
      .update({ ...stripImmutable(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select('*, companies:company_id (id, name)')
      .single();
    if (error) throw error;
    invalidateUsersCache();
    return data as CustomerUser;
  },

  async deleteCustomerUser(id: string): Promise<void> {
    const { error } = await supabase
      .from('customer_users')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
    invalidateUsersCache();
  },

  // ── Customer User Attachments ──────────────────────────────
  async getUserAttachments(userId: string): Promise<CustomerUserAttachment[]> {
    const { data, error } = await supabase
      .from('customer_user_attachments')
      .select('*')
      .eq('customer_user_id', userId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async uploadUserAttachment(userId: string, orgId: string, file: File): Promise<CustomerUserAttachment> {
    const ext = file.name.split('.').pop() ?? 'bin';
    const storagePath = `${orgId}/${userId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from(USER_BUCKET)
      .upload(storagePath, file, { contentType: file.type, upsert: false });
    if (uploadErr) throw new Error(`Storage upload failed: ${uploadErr.message}`);

    const { data: { publicUrl } } = supabase.storage.from(USER_BUCKET).getPublicUrl(storagePath);
    const userId_ = await requireCurrentUserId();

    const { data, error: insertErr } = await supabase
      .from('customer_user_attachments')
      .insert({
        customer_user_id: userId,
        org_id: orgId,
        file_name: file.name,
        file_type: file.type,
        storage_path: storagePath,
        url: publicUrl,
        created_by: userId_,
      })
      .select()
      .single();
    if (insertErr) { await supabase.storage.from(USER_BUCKET).remove([storagePath]); throw new Error(`DB insert failed: ${insertErr.message}`); }
    return data;
  },

  async deleteUserAttachment(attachment: CustomerUserAttachment): Promise<void> {
    await supabase.storage.from(USER_BUCKET).remove([attachment.storage_path]);
    const { error } = await supabase
      .from('customer_user_attachments')
      .delete()
      .eq('id', attachment.id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },
};
